import { memo, useCallback, useEffect, useMemo, useState } from 'react'
import { gridGenerationsApi, gridTemplatesApi, gridsApi, programsApi, seriesApi } from '../../lib/api.js'
import { weekMondayISO } from '../../lib/utils.js'
import { generationRangeLabel } from '../../lib/generationRange.js'
import { replayOriginLabel } from '../../lib/replayOrigin.js'
import { recordGridChange } from '../../lib/gridUndo.js'
import type {
  GenerationScope,
  ScheduleGrid,
  TemplateProposal,
} from '../../types/domain.js'
import { ModalShell } from '../common/ModalShell.js'
import './AgendaDialogs.css'
import { substitutionOriginId } from '../../lib/mirrorSubstitution.js'
import { unresolvedOverlapIds } from '../../lib/proposal-overlaps.js'
import { defaultPropagationTargets, sameSlotOccurrences } from '../../lib/slotOccurrences.js'
import { HttpError } from '../../lib/http.js'

/**
 * Parties du programme lui-même. Les segments portent aussi le pre-roll et le
 * post-roll, qui n'ont pas de numéro de partie et ne doivent pas apparaître
 * dans le découpage en parties.
 */
function partLabels(proposal: TemplateProposal): string[] {
  return proposal.segments
    .filter(segment => segment.partNumber !== null)
    .map(segment => `P${segment.partNumber}`)
}

/**
 * Créneau sans contenu nommé : ni programme choisi, ni inédit réservé, ni
 * reprise d'un inédit du même jour. C'est là que l'intention du modèle doit
 * prendre le relais du titre.
 */
function isUnresolved(proposal: TemplateProposal): boolean {
  return !proposal.programTitle && !proposal.pendingPremiere && !proposal.pendingPremiereReplayOf
}

/** Ancre DOM d'une carte de créneau, pour y ramener la liste. */
function proposalDomId(proposalId: string): string {
  return `template-proposal-${proposalId}`
}

/**
 * Créneaux désignés par un refus de dépôt. Le serveur rend, avec
 * `UNRESOLVED_PREVIEW`, la liste des débordements ; toute autre erreur n'en
 * désigne aucun.
 */
function rejectedProposalIds(error: unknown): string[] {
  if (!(error instanceof HttpError)) return []
  const body = error.body as { overlaps?: Array<{ proposalId?: unknown }> } | undefined
  return (body?.overlaps ?? [])
    .map(overlap => overlap.proposalId)
    .filter((id): id is string => typeof id === 'string')
}

/** Programme proposé en substitution, tel que lu au catalogue. */
type SubstituteProgram = {
  id: string
  title: string
  durationSec: number
}

/** Programme retenu à la main : seul son titre sert à l'affichage. */
type ChosenProgram = Pick<SubstituteProgram, 'id' | 'title'>

type Decision = {
  slotId: string
  proposalId: string
  action?: 'APPLY' | 'SKIP'
  conflictAction?: 'KEEP' | 'REPLACE'
  programId?: string
}

export const TemplateLoadDialog = memo(function TemplateLoadDialog(props: {
  channelId: string
  gridId: string | null
  date: string
  onApplied: (gridId: string, targetDate: string) => void
  onClose: () => void
}) {
  const [templates, setTemplates] = useState<ScheduleGrid[]>([])
  const [templateId, setTemplateId] = useState('')
  const [targetDate, setTargetDate] = useState(props.date)
  const [scope, setScope] = useState<GenerationScope>('WEEK')
  const [targetDescription, setTargetDescription] = useState('La grille de la semaine affichée sera utilisée.')
  const [generationId, setGenerationId] = useState<string | null>(null)
  const [proposals, setProposals] = useState<TemplateProposal[]>([])
  const [decisions, setDecisions] = useState<Record<string, Decision>>({})
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  // Substitution manuelle : le vivier d'un créneau est vide précisément quand le
  // contenu n'est pas encore tourné, donc le choix se fait sur tout le
  // catalogue. Le libellé retenu est mémorisé pour rester lisible après coup.
  const [pickerFor, setPickerFor] = useState<string | null>(null)
  const [pickerQuery, setPickerQuery] = useState('')
  const [pickerResults, setPickerResults] = useState<SubstituteProgram[]>([])
  const [pickerBusy, setPickerBusy] = useState(false)
  const [manualLabels, setManualLabels] = useState<Record<string, ChosenProgram>>({})
  const [confirmHoles, setConfirmHoles] = useState(false)
  // Report d'un choix sur les autres jours du même créneau : proposé juste
  // après une substitution, jamais appliqué sans que l'opérateur le valide.
  const [propagation, setPropagation] = useState<{
    sourceId: string
    program: ChosenProgram
    selected: Set<string>
  } | null>(null)
  // Partie « modèle désynchronisé » : le recalage à sec (`apply: false`) ne
  // modifie rien et dit combien de fenêtres du modèle ne correspondent plus aux
  // durées du catalogue. Rien n'est stocké — la réponse est recalculée à chaque
  // ouverture, donc toujours exacte, là où une empreinte en base dériverait.
  const [retimeGap, setRetimeGap] = useState<number | null>(null)
  // Créneaux désignés par le dernier refus de dépôt (`UNRESOLVED_PREVIEW`) :
  // le serveur dit lesquels débordent, l'écran les montre.
  const [rejectedIds, setRejectedIds] = useState<Set<string>>(() => new Set())

  /** Amène une carte de créneau au centre de la liste. */
  const revealProposal = useCallback((proposalId: string) => {
    document.getElementById(proposalDomId(proposalId))?.scrollIntoView({ block: 'center', behavior: 'smooth' })
  }, [])

  // L'annulation d'un dépôt vit dans l'espace Modèles de grille, pas ici : ce
  // dialogue ne sert qu'à simuler puis poser.
  const loadLists = useCallback(async () => {
    const templateList = await gridTemplatesApi.list(props.channelId)
    const validated = templateList.filter(item => item.status === 'VALIDATED')
    setTemplates(validated)
    setTemplateId(current => current || validated[0]?.id || '')
  }, [props.channelId])

  useEffect(() => { loadLists().catch(error => setError(error.message)) }, [loadLists])

  // Écart entre les fenêtres du modèle et les durées réelles du catalogue.
  // Purement informatif : le dépôt absorbe déjà l'écart en décalant, mais un
  // modèle qu'un recalage remettrait d'aplomb vaut mieux qu'une semaine de
  // créneaux qui glissent tous.
  useEffect(() => {
    if (!templateId) { setRetimeGap(null); return }
    let cancelled = false
    gridTemplatesApi.retime(templateId, { apply: false })
      .then(result => { if (!cancelled) setRetimeGap(result.changes.length) })
      // Un modèle qu'on ne peut pas mesurer ne doit pas empêcher de le déposer.
      .catch(() => { if (!cancelled) setRetimeGap(null) })
    return () => { cancelled = true }
  }, [templateId])

  const resolveTargetGrid = useCallback(async (): Promise<string | null> => {
    const targetMonday = weekMondayISO(targetDate)
    if (props.gridId && weekMondayISO(props.date) === targetMonday) return props.gridId

    const result = await gridsApi.list({
      channelId: props.channelId,
      gridType: 'WEEKLY',
      weekStart: targetMonday,
      limit: 50,
    })
    return (result.data as ScheduleGrid[])
      .filter(grid => grid.status !== 'ARCHIVED' && grid.weekStart && weekMondayISO(grid.weekStart) === targetMonday)
      .sort((left, right) =>
        new Date(right.updatedAt).getTime() - new Date(left.updatedAt).getTime()
      )[0]?.id ?? null
  }, [props.channelId, props.date, props.gridId, targetDate])

  const preview = useCallback(async () => {
    if (!templateId || !targetDate) return
    setBusy(true)
    setError(null)
    try {
      const targetGridId = await resolveTargetGrid()
      setTargetDescription(targetGridId
        ? 'Le modèle sera posé sur la grille de diffusion de cette semaine, uniquement sur les journées encore vides.'
        : 'Aucune grille n’existe pour cette semaine : la grille de diffusion sera créée lors de l’application.')
      const result = await gridTemplatesApi.preview(templateId, {
        targetDate,
        scope,
        targetGridId: targetGridId ?? undefined,
      })
      setGenerationId(result.generationId)
      setProposals(result.proposals)
      setManualLabels({})
      setPropagation(null)
      setConfirmHoles(false)
      setRejectedIds(new Set())
      setDecisions(Object.fromEntries(result.proposals.map(proposal => [
        proposal.proposalId,
        {
          slotId: proposal.slotId,
          proposalId: proposal.proposalId,
          action: 'APPLY',
          conflictAction: proposal.conflicts.length > 0 ? 'KEEP' : undefined,
        },
      ])))
    } catch (error: any) {
      setError(error.message)
    } finally {
      setBusy(false)
    }
  }, [resolveTargetGrid, scope, targetDate, templateId])

  const setDecision = useCallback((proposal: TemplateProposal, patch: Partial<Decision>) => {
    setConfirmHoles(false)
    // L'opérateur a agi : le refus du dépôt précédent n'est plus d'actualité,
    // le prochain essai le rejugera.
    setRejectedIds(current => (current.size === 0 ? current : new Set()))
    setDecisions(current => ({
      ...current,
      [proposal.proposalId]: { ...current[proposal.proposalId], slotId: proposal.slotId, proposalId: proposal.proposalId, ...patch },
    }))
  }, [])

  const apply = useCallback(async () => {
    if (!generationId) return
    setBusy(true)
    setError(null)
    try {
      // Un modèle pose une semaine entière d'un coup : c'est l'action pour
      // laquelle l'annulation compte le plus, et la grille visée peut n'exister
      // qu'après l'application — d'où la relecture guidée par la réponse.
      const result = await recordGridChange({
        label:   'chargement d’un modèle',
        gridIds: [await resolveTargetGrid()],
        date:    targetDate,
        gridIdsFromResult: (applied: { targetGridId: string }) => [applied.targetGridId],
      }, () => gridGenerationsApi.apply(generationId, Object.values(decisions)))
      props.onApplied(result.targetGridId, targetDate)
      props.onClose()
    } catch (error: any) {
      setError(error.message)
      const ids = rejectedProposalIds(error)
      setRejectedIds(new Set(ids))
      if (ids.length > 0) revealProposal(ids[0])
    } finally {
      setBusy(false)
    }
  }, [generationId, decisions, props, resolveTargetGrid, targetDate, revealProposal])

  // Recherche catalogue du sélecteur de substitution, relancée à la frappe.
  useEffect(() => {
    if (!pickerFor) return
    let cancelled = false
    setPickerBusy(true)
    const timer = window.setTimeout(() => {
      programsApi.list({ channelId: props.channelId, search: pickerQuery || undefined, limit: 25 })
        .then(result => {
          if (cancelled) return
          setPickerResults((result.data ?? []).map((program: any) => ({
            id: program.id,
            title: program.title,
            durationSec: program.durationSec,
          })))
        })
        .catch(error => { if (!cancelled) setError(error.message) })
        .finally(() => { if (!cancelled) setPickerBusy(false) })
    }, 250)
    return () => { cancelled = true; window.clearTimeout(timer) }
  }, [pickerFor, pickerQuery, props.channelId])

  const chooseSubstitute = useCallback((proposal: TemplateProposal, program: SubstituteProgram) => {
    setDecision(proposal, { programId: program.id, action: 'APPLY' })
    setManualLabels(current => ({ ...current, [proposal.proposalId]: program }))
    setPickerFor(null)
    setPickerQuery('')
  }, [setDecision])

  const clearSubstitute = useCallback((proposal: TemplateProposal) => {
    setDecision(proposal, { programId: undefined })
    setPropagation(current => (current?.sourceId === proposal.proposalId ? null : current))
    setManualLabels(current => {
      const next = { ...current }
      delete next[proposal.proposalId]
      return next
    })
  }, [setDecision])

  const createPlannedEdition = useCallback(async (proposal: TemplateProposal) => {
    if (!proposal.seriesId) return
    const label = proposal.seriesTitle ?? 'cette émission'
    const plannedDate = proposal.mediaLookupDate || proposal.startTime.slice(0, 10)
    if (!window.confirm(`Créer une nouvelle fiche pour « ${label} » prévue le ${new Date(`${plannedDate}T00:00:00`).toLocaleDateString('fr-FR')} ?\n\nLe média pourra être ajouté plus tard, mais la grille restera non validable tant qu’il manque.`)) return
    setBusy(true)
    setError(null)
    try {
      await seriesApi.createEdition(proposal.seriesId, {
        plannedFirstAirDate: plannedDate,
        durationSec: proposal.nominalDurationSec,
      })
      await preview()
    } catch (error: any) {
      setError(error.message)
    } finally {
      setBusy(false)
    }
  }, [preview])

  // Nombre de reprises du jour rattachées à chaque tête : une substitution sur
  // la tête les suit, ce que l'aperçu annonce avant l'application.
  const mirrorsByOrigin = useMemo(() => {
    const counts = new Map<string, number>()
    for (const proposal of proposals) {
      const origin = substitutionOriginId(proposal)
      if (origin) counts.set(origin, (counts.get(origin) ?? 0) + 1)
    }
    return counts
  }, [proposals])

  /** Contenu disponible pour ce créneau, substitution manuelle comprise. */
  const hasContent = useCallback((proposal: TemplateProposal) => (
    Boolean(decisions[proposal.proposalId]?.programId || proposal.programId || proposal.pendingPremiere)
  ), [decisions])

  /** Un créneau pose-t-il réellement un élément sur la grille ? */
  const producesItem = useCallback((proposal: TemplateProposal) => {
    const decision = decisions[proposal.proposalId]
    if (decision?.action === 'SKIP') return false
    // Un conflit conservé laisse l'élément déjà en place : le créneau n'est pas
    // posé, mais il n'y a pas de trou pour autant.
    if (proposal.conflicts.length > 0 && decision?.conflictAction !== 'REPLACE') return false
    return hasContent(proposal)
  }, [decisions, hasContent])

  // Une reprise n'a pas de programme à elle : elle vit ou meurt avec sa tête,
  // substitution comprise.
  const resolves = useCallback((proposal: TemplateProposal) => {
    if (producesItem(proposal)) return true
    const originId = substitutionOriginId(proposal)
    if (!originId) return false
    const origin = proposals.find(item => item.proposalId === originId)
    return origin ? producesItem(origin) : false
  }, [producesItem, proposals])

  // Compteur des créneaux non résolus : aucun contenu pour eux, ni en propre ni
  // par la tête qu'ils reprennent, indépendamment des décisions prises. C'est le
  // reste à traiter, là où les trous ne comptent que ce qu'on a demandé de poser.
  const lacksContent = useCallback((proposal: TemplateProposal) => {
    if (hasContent(proposal)) return false
    const originId = substitutionOriginId(proposal)
    const origin = originId ? proposals.find(item => item.proposalId === originId) : undefined
    return !origin || !hasContent(origin)
  }, [hasContent, proposals])
  const unresolvedProposals = useMemo(() => proposals.filter(lacksContent), [lacksContent, proposals])
  const unresolvedCount = unresolvedProposals.length

  // Un créneau étendu sur plusieurs jours pose une occurrence par jour. Après
  // un choix sur l'une, on propose de le reporter sur les autres : les jours
  // encore sans contenu sont cochés, ceux déjà résolus restent à cocher.
  const offerPropagation = useCallback((proposal: TemplateProposal, program: ChosenProgram) => {
    const occurrences = sameSlotOccurrences(proposals, proposal.proposalId)
    setPropagation(occurrences.length > 0
      ? { sourceId: proposal.proposalId, program, selected: defaultPropagationTargets(occurrences, lacksContent) }
      : null)
  }, [proposals, lacksContent])

  const togglePropagationTarget = useCallback((proposalId: string) => {
    setPropagation(current => {
      if (!current) return current
      const selected = new Set(current.selected)
      if (selected.has(proposalId)) selected.delete(proposalId)
      else selected.add(proposalId)
      return { ...current, selected }
    })
  }, [])

  const toggleAllPropagationTargets = useCallback((proposalIds: string[]) => {
    setPropagation(current => {
      if (!current) return current
      const allSelected = proposalIds.every(id => current.selected.has(id))
      return { ...current, selected: allSelected ? new Set() : new Set(proposalIds) }
    })
  }, [])

  const applyPropagation = useCallback(() => {
    if (!propagation) return
    const targets = proposals.filter(proposal => propagation.selected.has(proposal.proposalId))
    for (const target of targets) setDecision(target, { programId: propagation.program.id, action: 'APPLY' })
    setManualLabels(current => ({
      ...current,
      ...Object.fromEntries(targets.map(target => [target.proposalId, propagation.program])),
    }))
    setPropagation(null)
  }, [propagation, proposals, setDecision])

  // Trous : créneaux qu'on a demandé d'appliquer mais qui ne poseront rien.
  // Ils n'empêchent plus l'application, ils sont listés avant confirmation.
  const holes = useMemo(() => proposals.filter(proposal => {
    const decision = decisions[proposal.proposalId]
    if (decision?.action === 'SKIP') return false
    if (proposal.conflicts.length > 0 && decision?.conflictAction !== 'REPLACE') return false
    return !resolves(proposal)
  }), [proposals, decisions, resolves])

  // Créneaux que le serveur a décalés parce que la durée réelle de leur contenu
  // dépasse la fenêtre saisie dans le modèle. Ce n'est pas une erreur — c'est la
  // règle, la durée réelle fait foi — mais une heure d'antenne qui glisse se
  // récapitule avant le dépôt, jamais en silence.
  const shifts = useMemo(() => proposals
    .filter(proposal => decisions[proposal.proposalId]?.action !== 'SKIP')
    .map(proposal => ({
      proposal,
      warning: proposal.warnings.find(warning => warning.startsWith('Décalé de')),
    }))
    .filter((entry): entry is { proposal: TemplateProposal; warning: string } => Boolean(entry.warning)),
  [proposals, decisions])

  // Réserves à annoncer avant le dépôt : ce qui ne sera pas posé, et ce qui ne
  // sera pas posé à l'heure du modèle. Les deux méritent une confirmation, mais
  // pas le même mot — un trou se subit (« malgré »), un décalage est la règle
  // voulue et s'accompagne (« avec »).
  const depositCaveats = useMemo(() => [
    holes.length > 0 ? `${holes.length} trou(s)` : null,
    shifts.length > 0 ? `${shifts.length} décalage(s)` : null,
  ].filter((entry): entry is string => Boolean(entry)), [holes, shifts])

  const applyLabel = useMemo(() => {
    if (depositCaveats.length === 0) return 'Appliquer la génération'
    if (holes.length === 0) return `Appliquer avec ${shifts.length} décalage(s)`
    return `Appliquer malgré ${depositCaveats.join(' et ')}`
  }, [depositCaveats, holes, shifts])

  // Un chevauchement figé par la prévisualisation porte sur l'épisode choisi
  // automatiquement. Dès que l'opérateur substitue, c'est le dépôt qui
  // rearbitre sur la durée réelle du remplaçant : l'avertissement n'est plus
  // une décision à trancher ici, sinon substituer un épisode compatible ne
  // débloquerait jamais rien.
  const rearbitrated = useCallback((proposal: TemplateProposal) => (
    Boolean(decisions[proposal.proposalId]?.programId)
  ), [decisions])

  // Chevauchements encore réels une fois les créneaux ignorés retirés, calculés
  // comme le fait le dépôt. Ignorer le créneau débordé rend sa place à celui du
  // dessus : le serveur l'accepte déjà, et l'écran le refusait encore.
  const liveOverlaps = useMemo(
    () => unresolvedOverlapIds(proposals, proposalId => decisions[proposalId]?.action === 'SKIP'),
    [proposals, decisions],
  )

  /** Chevauchement figé par la prévisualisation mais résorbé par les décisions. */
  const overlapResolved = useCallback((proposal: TemplateProposal) => (
    !liveOverlaps.has(proposal.proposalId)
  ), [liveOverlaps])

  // Bloqueurs durs : l'application les refuse côté serveur, il faut trancher.
  const blockers = useMemo(() => proposals.filter(proposal => {
    if (decisions[proposal.proposalId]?.action === 'SKIP') return false
    if (proposal.conflicts.some(item => ['ON_AIR', 'DONE'].includes(item.status))) return true
    if (rearbitrated(proposal)) return false
    return liveOverlaps.has(proposal.proposalId)
  }), [proposals, decisions, rearbitrated, liveOverlaps])

  const blockerIds = useMemo(() => new Set(blockers.map(proposal => proposal.proposalId)), [blockers])

  return (
    <ModalShell title="Charger un modèle de grille" onClose={props.onClose} closeDisabled={busy} size="wide" className="agenda-dialog agenda-template-load">
        <div className="agenda-dialog__subtitle">Simulation déterministe avant toute modification</div>

        <div className="agenda-template-load__toolbar">
          <label className="agenda-dialog__field agenda-template-load__template-field">
            Modèle
            <select className="tp-input" value={templateId} onChange={event => { setTemplateId(event.target.value); setProposals([]); setGenerationId(null) }}>
              {templates.length === 0 && <option value="">Aucun modèle validé</option>}
              {templates.map(template => <option key={template.id} value={template.id}>{template.name} · v{template.version}</option>)}
            </select>
          </label>
          <label className="agenda-dialog__field">
            Date de début
            <input
              type="date"
              className="tp-input"
              value={targetDate}
              onChange={event => { setTargetDate(event.target.value); setProposals([]); setGenerationId(null); setTargetDescription('La grille correspondante sera recherchée lors de la prévisualisation.') }}
            />
          </label>
          <button
            className="tp-button"
            // Le lundi de la semaine affichée : en portée « Semaine », c'est la
            // semaine complète en un clic.
            onClick={() => { setTargetDate(weekMondayISO(props.date)); setProposals([]); setGenerationId(null); setTargetDescription('La grille de la semaine affichée sera utilisée.') }}
          >Semaine affichée</button>
          <label className="agenda-dialog__field">
            Portée
            <select className="tp-input" value={scope} onChange={event => { setScope(event.target.value as GenerationScope); setProposals([]); setGenerationId(null) }}>
              <option value="DAY">Ce jour</option>
              <option value="WEEK">Semaine</option>
            </select>
          </label>
          <button className="tp-button tp-button--primary" disabled={busy || !templateId || !targetDate} onClick={preview}>Prévisualiser</button>
        </div>

        <div className="agenda-template-load__summary">
          {targetDate ? generationRangeLabel(targetDate, scope) : '—'} · {targetDescription}
          {proposals.length > 0 && (unresolvedCount > 0 ? (
            // Cliquable : amène au premier créneau encore sans contenu.
            <button
              type="button"
              className="agenda-template-load__unresolved is-warning"
              onClick={() => revealProposal(unresolvedProposals[0].proposalId)}
              title="Aller au premier créneau non résolu"
            >
              {unresolvedCount} créneau(x) non résolu(s) sur {proposals.length} →
            </button>
          ) : (
            <span className="agenda-template-load__unresolved">Les {proposals.length} créneaux sont résolus</span>
          ))}
        </div>

        {error && <div className="agenda-template-load__error" role="alert">{error}</div>}

        {retimeGap !== null && retimeGap > 0 && (
          <div className="agenda-template-load__retime-gap">
            {retimeGap} fenêtre(s) de ce modèle ne correspondent plus aux durées réelles du catalogue.
            Le dépôt absorbera l’écart en décalant les créneaux concernés ; un recalage du modèle
            (Modèles de grille → Recaler sur les durées réelles) remettrait ses heures d’aplomb.
          </div>
        )}

        <div className="agenda-template-load__content">
          {proposals.length > 0 ? (
            <div className="agenda-template-load__proposals">
              {proposals.map(proposal => {
                const decision = decisions[proposal.proposalId] ?? { slotId: proposal.slotId, proposalId: proposal.proposalId }
                const immutable = proposal.conflicts.some(item => ['ON_AIR', 'DONE'].includes(item.status))
                // Deux états à voir d'un coup d'œil : rien à poser ici (à
                // traiter), ou un contenu choisi à la main à la place du modèle.
                const pending = lacksContent(proposal)
                const substituted = !pending && isUnresolved(proposal) && Boolean(decision.programId)
                const cardClass = [
                  'agenda-template-load__proposal',
                  blockerIds.has(proposal.proposalId) || rejectedIds.has(proposal.proposalId) ? 'is-blocking' : '',
                  pending && decision.action !== 'SKIP' ? 'is-unresolved' : '',
                  substituted ? 'is-substituted' : '',
                ].filter(Boolean).join(' ')
                return (
                  <article key={proposal.proposalId} id={proposalDomId(proposal.proposalId)} className={cardClass}>
                    <div>
                      <div className="agenda-template-load__proposal-time">{new Date(proposal.startTime).toLocaleString('fr-FR', { weekday: 'short', hour: '2-digit', minute: '2-digit' })}</div>
                      <div className="agenda-template-load__proposal-meta">{Math.round(proposal.durationSec / 60)} min · {proposal.airingKind}</div>
                      {rejectedIds.has(proposal.proposalId)
                        ? <span className="agenda-template-load__badge is-danger">Déborde</span>
                        : pending && decision.action !== 'SKIP'
                          ? <span className="agenda-template-load__badge is-warning">Non résolu</span>
                          : substituted
                            ? <span className="agenda-template-load__badge is-info">Remplacé</span>
                            : null}
                    </div>
                    <div>
                      <div className="agenda-template-load__proposal-title">{proposal.programTitle ?? proposal.pendingPremiere?.title ?? manualLabels[proposal.proposalId]?.title ?? 'Créneau non résolu'}</div>
                      <div className="agenda-template-load__proposal-reason">{proposal.reason}</div>
                      {isUnresolved(proposal) && proposal.intent.label && <div className="agenda-template-load__intent">
                        Prévu ici : <strong>{proposal.intent.label}</strong>
                      </div>}
                      {proposal.itemType === 'LIVE' && <div className="agenda-template-load__proposal-meta">
                        Direct{proposal.liveSourceName ? ` · ${proposal.liveSourceName}` : ''} · démarrage {proposal.liveStartMode === 'MANUAL' ? 'manuel' : 'automatique'}
                      </div>}
                      {proposal.itemType === 'PROGRAM' && !proposal.pendingPremiere && !proposal.pendingPremiereReplayOf && <div className="agenda-template-load__proposal-meta">
                        {proposal.rerunCount} rediffusion(s){proposal.lastAiredAt ? ` · dernière le ${new Date(proposal.lastAiredAt).toLocaleDateString('fr-FR')}` : ''}
                      </div>}
                      {proposal.pendingPremiere && <div className="agenda-template-load__proposal-meta">
                        Fiche créée à l’application · média à associer ensuite
                      </div>}
                      {proposal.pendingPremiereReplayOf && <div className="agenda-template-load__proposal-meta">
                        {replayOriginLabel(proposal, proposals.find(item => item.proposalId === proposal.pendingPremiereReplayOf))}
                      </div>}
                      {(() => {
                        const originId = substitutionOriginId(proposal)
                        const substitute = originId ? manualLabels[originId] : undefined
                        return substitute
                          ? <div className="agenda-template-load__proposal-meta agenda-template-load__follows">
                              ↪ suit la substitution « {substitute.title} »
                            </div>
                          : null
                      })()}
                      {(mirrorsByOrigin.get(proposal.proposalId) ?? 0) > 0 && <div className="agenda-template-load__proposal-meta">
                        {mirrorsByOrigin.get(proposal.proposalId)} reprise(s) de la semaine suivront ce choix
                      </div>}
                      {partLabels(proposal).length > 1 && <div className="agenda-template-load__proposal-meta">
                        {partLabels(proposal).join(' → ')}
                      </div>}
                      {(proposal.junctionJingle || proposal.preRoll || proposal.postRoll) && <div className="agenda-template-load__proposal-meta">
                        {[
                          proposal.junctionJingle && `liaison : ${proposal.junctionJingle.title}`,
                          proposal.preRoll && `avant : ${proposal.preRoll.title}`,
                          proposal.postRoll && `après : ${proposal.postRoll.title}`,
                        ].filter(Boolean).join(' · ')}
                      </div>}
                      {proposal.warnings.map(warning => {
                        const overlap = warning.startsWith('Chevauchement')
                        const stale = overlap && (rearbitrated(proposal) || overlapResolved(proposal))
                        // Un décalage n'est pas un défaut : la durée réelle fait
                        // foi, et pousser le créneau suivant est le comportement
                        // voulu. Le crier en orange ferait chercher une erreur
                        // là où il n'y en a pas.
                        const shifted = warning.startsWith('Décalé de')
                        return (
                          <div key={warning} className={`agenda-template-load__warning ${stale ? 'is-resolved' : ''} ${shifted ? 'is-shift' : ''}`}>
                            {shifted ? '⤓ ' : '⚠ '}{warning}
                            {overlap && rearbitrated(proposal) && ' — recalculé au dépôt sur l’épisode choisi'}
                            {overlap && !rearbitrated(proposal) && overlapResolved(proposal) && ' — résorbé : le créneau suivant est ignoré'}
                          </div>
                        )
                      })}
                      {proposal.conflicts.length > 0 && <div className={`agenda-template-load__conflict ${immutable ? 'is-danger' : ''}`}>{proposal.conflicts.length} conflit(s) avec la grille</div>}
                      {proposal.canCreateEdition && <button className="tp-button tp-button--info agenda-template-load__create-edition" disabled={busy} onClick={() => createPlannedEdition(proposal)}>Créer l’édition prévue</button>}
                    </div>
                    <div className="agenda-template-load__decisions">
                      {proposal.itemType === 'PROGRAM' && (
                        manualLabels[proposal.proposalId] ? (
                          <div className="agenda-template-load__substitute">
                            <span className="agenda-template-load__substitute-name" title={manualLabels[proposal.proposalId].title}>↪ {manualLabels[proposal.proposalId].title}</span>
                            <button className="tp-button" disabled={busy} onClick={() => clearSubstitute(proposal)}>Rétablir</button>
                          </div>
                        ) : (
                          <button
                            className="tp-button"
                            disabled={busy}
                            onClick={() => { setPickerFor(pickerFor === proposal.proposalId ? null : proposal.proposalId); setPickerQuery(proposal.intent.searchHint ?? '') }}
                          >{pickerFor === proposal.proposalId ? 'Fermer' : 'Choisir au catalogue…'}</button>
                        )
                      )}
                      {pickerFor === proposal.proposalId && (
                        <div className="agenda-template-load__picker">
                          <input
                            className="tp-input"
                            autoFocus
                            placeholder="Rechercher un programme…"
                            value={pickerQuery}
                            onChange={event => setPickerQuery(event.target.value)}
                          />
                          <div className="agenda-template-load__picker-list">
                            {pickerBusy && <div className="agenda-template-load__proposal-meta">Recherche…</div>}
                            {!pickerBusy && pickerResults.length === 0 && <div className="agenda-template-load__proposal-meta">Aucun programme</div>}
                            {pickerResults.map(program => (
                              <button
                                key={program.id}
                                className="agenda-template-load__picker-row"
                                onClick={() => { chooseSubstitute(proposal, program); offerPropagation(proposal, program) }}
                              >
                                <span>{program.title}</span>
                                <span className="agenda-template-load__proposal-meta">{Math.round(program.durationSec / 60)} min</span>
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                      {proposal.itemType === 'PROGRAM' && proposal.alternatives.length > 0 && (
                        <select className="tp-input" value={decision.programId ?? proposal.programId ?? ''} onChange={event => {
                          const programId = event.target.value || undefined
                          setDecision(proposal, { programId, action: programId ? 'APPLY' : 'SKIP' })
                          const alternative = proposal.alternatives.find(item => item.id === programId)
                          if (alternative) offerPropagation(proposal, { id: alternative.id, title: alternative.title })
                          else setPropagation(current => (current?.sourceId === proposal.proposalId ? null : current))
                        }}>
                          <option value="">Choisir une autre édition…</option>
                          {proposal.alternatives.map(item => <option key={item.id} value={item.id}>
                            {item.title} · {item.rerunCount} rediffusion(s){item.lastAiredAt ? ` · dernière le ${new Date(item.lastAiredAt).toLocaleDateString('fr-FR')}` : ''}
                          </option>)}
                        </select>
                      )}
                      {proposal.conflicts.length > 0 && !immutable && (
                        <select className="tp-input" value={decision.conflictAction ?? 'KEEP'} onChange={event => setDecision(proposal, { conflictAction: event.target.value as 'KEEP' | 'REPLACE' })}>
                          <option value="KEEP">Conserver l’existant</option>
                          <option value="REPLACE">Remplacer l’existant</option>
                        </select>
                      )}
                      <select className="tp-input" value={decision.action ?? 'APPLY'} onChange={event => setDecision(proposal, { action: event.target.value as 'APPLY' | 'SKIP' })}>
                        <option value="APPLY" disabled={immutable}>Appliquer le créneau</option>
                        <option value="SKIP">Ignorer ce créneau</option>
                      </select>
                    </div>
                    {propagation?.sourceId === proposal.proposalId && (() => {
                      const occurrences = sameSlotOccurrences(proposals, proposal.proposalId)
                      const occurrenceIds = occurrences.map(item => item.proposalId)
                      const allSelected = occurrenceIds.every(id => propagation.selected.has(id))
                      return (
                        <div className="agenda-template-load__propagation" role="group" aria-label="Reporter sur les autres jours du créneau">
                          <div className="agenda-template-load__propagation-head">
                            <span>Reporter « {propagation.program.title} » sur les autres jours de ce créneau :</span>
                            <button type="button" className="tp-button" onClick={() => toggleAllPropagationTargets(occurrenceIds)}>
                              {allSelected ? 'Aucune' : 'Toutes'}
                            </button>
                          </div>
                          <div className="agenda-template-load__propagation-list">
                            {occurrences.map(occurrence => {
                              const unresolved = lacksContent(occurrence)
                              const current = manualLabels[occurrence.proposalId]?.title ?? occurrence.programTitle ?? occurrence.pendingPremiere?.title
                              return (
                                <label key={occurrence.proposalId} className="agenda-template-load__propagation-row">
                                  <input
                                    type="checkbox"
                                    checked={propagation.selected.has(occurrence.proposalId)}
                                    onChange={() => togglePropagationTarget(occurrence.proposalId)}
                                  />
                                  <span className="agenda-template-load__proposal-time">
                                    {new Date(occurrence.startTime).toLocaleString('fr-FR', { weekday: 'short', hour: '2-digit', minute: '2-digit' })}
                                  </span>
                                  <span className={unresolved ? 'agenda-template-load__propagation-state is-warning' : 'agenda-template-load__propagation-state'}>
                                    {unresolved ? 'Non résolu' : `Déjà prévu : ${current ?? '—'}`}
                                  </span>
                                </label>
                              )
                            })}
                          </div>
                          <div className="agenda-template-load__propagation-actions">
                            <button type="button" className="tp-button" onClick={() => setPropagation(null)}>Ne rien reporter</button>
                            <button
                              type="button"
                              className="tp-button tp-button--info"
                              disabled={propagation.selected.size === 0}
                              onClick={applyPropagation}
                            >Reporter sur {propagation.selected.size} jour(s)</button>
                          </div>
                        </div>
                      )
                    })()}
                  </article>
                )
              })}
            </div>
          ) : (
            <section>
              <div className="agenda-dialog__empty agenda-template-load__prompt">Choisissez un modèle puis lancez la prévisualisation.</div>
            </section>
          )}
        </div>

        {proposals.length > 0 && confirmHoles && holes.length > 0 && (
          <div className="agenda-template-load__holes" role="alert">
            <strong>{holes.length} créneau(x) resteront vides sur la grille :</strong>
            <ul className="agenda-template-load__holes-list">
              {holes.slice(0, 12).map(hole => (
                <li key={hole.proposalId}>
                  {new Date(hole.startTime).toLocaleString('fr-FR', { weekday: 'short', hour: '2-digit', minute: '2-digit' })}
                  {' · '}{hole.programTitle ?? hole.intent.label ?? 'Créneau non résolu'}
                  {hole.pendingPremiereReplayOf ? ' (reprise sans tête)' : ''}
                </li>
              ))}
              {holes.length > 12 && <li>… et {holes.length - 12} autre(s)</li>}
            </ul>
          </div>
        )}

        {proposals.length > 0 && confirmHoles && shifts.length > 0 && (
          <div className="agenda-template-load__shifts" role="alert">
            <strong>{shifts.length} créneau(x) seront décalés : contenu réel plus long que la fenêtre du modèle, ou jingle de liaison ajouté devant.</strong>
            <ul className="agenda-template-load__holes-list">
              {shifts.slice(0, 12).map(({ proposal, warning }) => {
                // L'avertissement du serveur porte déjà « ancienne → nouvelle » :
                // répéter la nouvelle heure devant ferait lire trois heures pour
                // un seul déplacement.
                const move = warning.replace(/^Décalé de /, '').replace(/ : .*$/, '')
                return (
                  <li key={proposal.proposalId}>
                    {new Date(proposal.startTime).toLocaleDateString('fr-FR', { weekday: 'short' })}
                    {' · '}{proposal.programTitle ?? proposal.intent.label ?? 'Créneau'}
                    {' · '}{move}
                  </li>
                )
              })}
              {shifts.length > 12 && <li>… et {shifts.length - 12} autre(s)</li>}
            </ul>
            <small>Les rendez-vous d’antenne n’ont pas bougé ; seuls les créneaux ordinaires ont glissé.</small>
          </div>
        )}

        {proposals.length > 0 && (
          <footer className="agenda-template-load__footer">
            <span className={`agenda-template-load__footer-status ${blockers.length > 0 ? 'is-danger' : ''}`}>
              {blockers.length > 0
                ? `${blockers.length} décision(s) bloquante(s)`
                : [
                    `${proposals.length} créneau(x) simulé(s)`,
                    unresolvedCount > 0 ? `${unresolvedCount} non résolu(s)` : null,
                    holes.length > 0 ? `${holes.length} trou(s)` : null,
                    shifts.length > 0 ? `${shifts.length} décalage(s)` : null,
                  ].filter(Boolean).join(' · ')}
            </span>
            <div className="agenda-template-load__footer-spacer" />
            <button className="tp-button" onClick={() => { setProposals([]); setGenerationId(null); setConfirmHoles(false) }}>Retour</button>
            {depositCaveats.length > 0 && !confirmHoles ? (
              <button
                className="tp-button tp-button--primary"
                disabled={busy || blockers.length > 0}
                onClick={() => setConfirmHoles(true)}
              >Appliquer la génération</button>
            ) : (
              <button
                className="tp-button tp-button--primary"
                disabled={busy || blockers.length > 0}
                onClick={apply}
              >{applyLabel}</button>
            )}
          </footer>
        )}
    </ModalShell>
  )
})
